import { useEffect, useState } from 'react';
import type { PaymentProviderStateDto } from '@ecom/contracts';
import { usePaymentsStore } from '@/stores/payments.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { SETTINGS_PAGES } from '@/lib/settings-pages';
import { KeyIcon } from '@/components/icons';
import { Sheet, Tabs } from '@/components/page';
import { Alert, Button, Card, Field, Input, Pill, Skeleton, Switch } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';
import { OwnerOnly, SchemaSections } from '@/pages/settings/schema';

type PaymentsTab = 'gateways' | 'cod';

/**
 * Payment providers, plug and play (ADR-025).
 *
 * Choosing a processor is a commercial decision a store makes after it is
 * deployed — often after it is already selling — so it happens here rather than
 * in an environment variable that needs a release.
 *
 * Two things this screen refuses to do:
 *
 *  - **Show a stored secret.** The API returns `••••3f21` and a flag; a field
 *    that shows a key is a field that leaks one over a shoulder or a screen
 *    share. Typing a new value replaces it; leaving it blank keeps what is
 *    stored.
 *  - **Pretend an unfinished provider is ready.** A driver that has not shipped
 *    says so, and one missing a key names the key rather than greying out a
 *    switch and leaving the operator to guess.
 *
 * The gateways are a grid of cards, one switch each, with the keys behind a
 * side panel; cash on delivery's rules sit on the second tab, beside them.
 */
export function PaymentsPage() {
  const { providers, loading, saving, error, load, update } = usePaymentsStore();
  const t = useT();
  const [tab, setTab] = useState<PaymentsTab>('gateways');
  const [configuring, setConfiguring] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  // Read from the list on every render, so the panel shows what a save returned.
  const panel = providers.find((provider) => provider.key === configuring) ?? null;

  return (
    <SettingsPage
      title="paymentProviders"
      subtitle="settingsPaymentsHint"
      tabs={
        <Tabs
          label={t('paymentProviders')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'gateways', label: t('paymentGateways') },
            { id: 'cod', label: t('paymentCod') },
          ]}
        />
      }
    >
      {tab === 'cod' ? (
        <OwnerOnly>
          <SchemaSections keys={SETTINGS_PAGES.payments} />
        </OwnerOnly>
      ) : (
        <>
          <p className="text-sm text-ink-muted">{t('paymentProvidersSubtitle')}</p>

          {error ? <Alert>{error}</Alert> : null}

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-44" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {providers.map((provider) => (
                <ProviderCard
                  key={provider.key}
                  provider={provider}
                  saving={saving}
                  onUpdate={(patch) => update(provider.key, patch)}
                  onConfigure={() => setConfiguring(provider.key)}
                />
              ))}
            </div>
          )}

          {panel ? (
            <CredentialsSheet
              key={panel.key}
              provider={panel}
              saving={saving}
              error={error}
              onClose={() => setConfiguring(null)}
              onUpdate={(patch) => update(panel.key, patch)}
            />
          ) : null}
        </>
      )}
    </SettingsPage>
  );
}

type ProviderPatch = {
  enabled?: boolean;
  testMode?: boolean;
  credentials?: Record<string, string>;
  methods?: Record<string, boolean>;
};

function ProviderCard({
  provider,
  saving,
  onUpdate,
  onConfigure,
}: {
  provider: PaymentProviderStateDto;
  saving: boolean;
  onUpdate: (patch: ProviderPatch) => Promise<boolean>;
  onConfigure: () => void;
}) {
  const t = useT();

  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between gap-3 px-5 pt-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{provider.name}</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            {t(`paymentKind${capitalize(provider.kind)}` as MessageKey)}
          </p>
        </div>
        <Switch
          label={`${t('enabled')} — ${provider.name}`}
          checked={provider.enabled}
          disabled={saving || !provider.implemented}
          onChange={(enabled) => void onUpdate({ enabled })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 px-5 pt-3">
        {!provider.implemented ? (
          <Pill tone="neutral">{t('notShippedYet')}</Pill>
        ) : provider.enabled ? (
          <Pill tone="success">{t('enabled')}</Pill>
        ) : (
          <Pill tone="neutral">{t('inactive')}</Pill>
        )}
        {provider.testMode && provider.supportsTestMode && provider.enabled ? (
          <Pill tone="warning">{t('testMode')}</Pill>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-x-2 gap-y-1 px-5 pt-3 text-xs text-ink-muted">
        {provider.supportsRefunds ? <span>{t('supportsRefunds')}</span> : null}
        {provider.supportsWebhooks ? <span>· {t('supportsWebhooks')}</span> : null}
        {provider.redirects ? <span>· {t('redirectsShopper')}</span> : null}
        {provider.docsUrl ? (
          <a
            href={provider.docsUrl}
            target="_blank"
            rel="noreferrer"
            dir="ltr"
            className="max-w-full truncate underline hover:text-ink"
          >
            {provider.docsUrl}
          </a>
        ) : null}
      </div>

      {provider.methods.length > 0 && provider.implemented ? (
        <ProviderMethods provider={provider} saving={saving} onUpdate={onUpdate} />
      ) : null}

      {provider.missingCredentials.length > 0 && provider.implemented ? (
        <div className="px-5 pt-3">
          {/* Named, not implied: "why can I not switch this on" is the question
              the screen exists to answer. */}
          <Alert>
            {t('missingCredentials')}: <span dir="ltr">{provider.missingCredentials.join(', ')}</span>
          </Alert>
        </div>
      ) : null}

      <div className="mt-auto pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3">
          {provider.credentials.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('noCredentialsNeeded')}</p>
          ) : provider.supportsTestMode ? (
            <div className="flex items-center gap-2 text-sm text-ink">
              <Switch
                label={`${t('testMode')} — ${provider.name}`}
                checked={provider.testMode}
                disabled={saving}
                onChange={(testMode) => void onUpdate({ testMode })}
              />
              {t('testMode')}
            </div>
          ) : (
            <span />
          )}

          {provider.credentials.length > 0 ? (
            <Button variant="secondary" size="sm" onClick={onConfigure}>
              <KeyIcon className="size-4" />
              {t('configure')}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/**
 * Which of a provider's ways to pay the checkout offers (ADR-126) — HyperPay's
 * mada and Visa / Mastercard, one switch each, in the order the shopper sees
 * them. The API refuses the last one off while the provider is on, and says so.
 */
function ProviderMethods({
  provider,
  saving,
  onUpdate,
}: {
  provider: PaymentProviderStateDto;
  saving: boolean;
  onUpdate: (patch: ProviderPatch) => Promise<boolean>;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  return (
    <div className="mx-5 mt-3 rounded-lg border border-line px-3 py-2.5">
      <p className="text-xs font-medium text-ink">{t('paymentMethodsOffered')}</p>
      <p className="mt-0.5 text-xs text-ink-muted">{t('paymentMethodsOfferedHint')}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {provider.methods.map((method) => (
          <li key={method.key} className="flex items-center justify-between gap-3 text-sm text-ink">
            <span>{method.name[locale] ?? method.name.en}</span>
            <Switch
              label={`${method.name[locale] ?? method.name.en} — ${provider.name}`}
              checked={method.enabled}
              disabled={saving}
              onChange={(enabled) => void onUpdate({ methods: { [method.key]: enabled } })}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The keys, in a panel beside the grid.
 *
 * What is typed lives only in this panel: it is cleared on a successful save
 * and dropped when the panel closes, so a key is never kept in memory longer
 * than the moment it takes to send it.
 */
function CredentialsSheet({
  provider,
  saving,
  error,
  onClose,
  onUpdate,
}: {
  provider: PaymentProviderStateDto;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onUpdate: (patch: ProviderPatch) => Promise<boolean>;
}) {
  const t = useT();
  const [draft, setDraft] = useState<Record<string, string>>({});
  // The store's error is shared by the page; only one this panel caused belongs in it.
  const [tried, setTried] = useState(false);

  const dirty = Object.values(draft).some((value) => value !== '');

  return (
    <Sheet
      open
      title={provider.name}
      subtitle={t(`paymentKind${capitalize(provider.kind)}` as MessageKey)}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || !dirty}
            onClick={async () => {
              setTried(true);
              const ok = await onUpdate({ credentials: draft });
              // Cleared on success only: a failed save must not lose what was
              // typed, and a successful one must not keep a key in memory.
              if (ok) {
                setDraft({});
                onClose();
              }
            }}
          >
            {saving ? t('saving') : t('saveKeys')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {tried && error ? <Alert>{error}</Alert> : null}

        {provider.missingCredentials.length > 0 && provider.implemented ? (
          <Alert>
            {t('missingCredentials')}: <span dir="ltr">{provider.missingCredentials.join(', ')}</span>
          </Alert>
        ) : null}

        {provider.credentials.map((field) => (
          <Field
            key={field.key}
            label={field.key}
            htmlFor={`${provider.key}-${field.key}`}
            hint={
              field.hasValue
                ? `${t('stored')}: ${field.preview ?? '••••'}`
                : (field.hint ?? (field.required ? t('required') : t('optional')))
            }
          >
            <Input
              id={`${provider.key}-${field.key}`}
              dir="ltr"
              type={field.secret ? 'password' : 'text'}
              autoComplete="off"
              placeholder={field.hasValue ? t('leaveBlankToKeep') : ''}
              value={draft[field.key] ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, [field.key]: event.target.value }))}
            />
          </Field>
        ))}
      </div>
    </Sheet>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
